import React from 'react';
import { Box, styled } from '@mui/material';
import { MusicNote as MusicNoteIcon } from '@mui/icons-material';

export type IllustrationFileType = 'pdf' | 'doc' | 'ppt' | 'html' | 'audio' | 'img' | 'video' | 'view' | 'archive';

const Paper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'shape' && prop !== 'size',
})<{ shape: 'portrait' | 'landscape' | 'window' | 'thumbnail'; size?: 'normal' | 'compact' }>(
  ({ theme, shape, size }) => {
    const isLight = theme.palette.mode === 'light';
    const shadow = isLight
      ? '0 1px 2px rgba(14,34,64,.10), 0 8px 18px rgba(14,34,64,.15), 0 24px 48px rgba(14,34,64,.12)'
      : '0 1px 2px rgba(0,0,0,.5), 0 8px 18px rgba(0,0,0,.5), 0 24px 48px rgba(0,0,0,.4)';
    const sizeKey = size ?? 'normal';
    const dimsBySize = {
      normal: {
        portrait:  { width: 76, height: 96,  rotate: '-2deg',   hoverRotate: '-1deg',   radius: '4px 4px 6px 6px' },
        landscape: { width: 96, height: 66,  rotate: '-1.5deg', hoverRotate: '-0.5deg', radius: '4px 4px 6px 6px' },
        window:    { width: 96, height: 74,  rotate: '-1.5deg', hoverRotate: '-0.5deg', radius: '6px' },
        thumbnail: { width: 96, height: 66,  rotate: '-1deg',   hoverRotate: '0deg',    radius: '6px' },
      },
      compact: {
        portrait:  { width: 56, height: 72,  rotate: '-2deg',   hoverRotate: '-1deg',   radius: '4px 4px 6px 6px' },
        landscape: { width: 72, height: 50,  rotate: '-1.5deg', hoverRotate: '-0.5deg', radius: '4px 4px 6px 6px' },
        window:    { width: 72, height: 56,  rotate: '-1.5deg', hoverRotate: '-0.5deg', radius: '6px' },
        thumbnail: { width: 72, height: 50,  rotate: '-1deg',   hoverRotate: '0deg',    radius: '6px' },
      },
    };
    const dims = dimsBySize[sizeKey][shape];

    return {
      background: '#FFFFFF',
      width: dims.width,
      height: dims.height,
      borderRadius: dims.radius,
      boxShadow: shadow,
      position: 'relative',
      overflow: 'hidden',
      transform: `rotate(${dims.rotate})`,
      transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
      // Animate when the enclosing card is hovered (not just the banner).
      // The illustration is always rendered inside a <Card>, so MuiCard-root
      // is its closest hoverable ancestor.
      '.MuiCard-root:hover &': {
        transform: `rotate(${dims.hoverRotate}) translateY(-3px) scale(1.04)`,
      },
    };
  }
);

const lineColor = '#DDE3EC';

const Lines: React.FC<{ children: React.ReactNode; padding?: number; gap?: number }> = ({ children, padding = 8, gap = 5 }) => (
  <Box sx={{ padding: `${padding}px`, display: 'flex', flexDirection: 'column', gap: `${gap}px` }}>{children}</Box>
);

const Line: React.FC<{ width: '100%' | '80%' | '60%' | '50%'; height?: number; bg?: string }> = ({ width, height = 4, bg = lineColor }) => (
  <Box sx={{ height: `${height}px`, width, background: bg, borderRadius: '2px' }} />
);

export const DocumentIllustration: React.FC<{ fileType: IllustrationFileType; size?: 'normal' | 'compact' }> = ({ fileType, size }) => {
  switch (fileType) {
    case 'pdf':
      return (
        <Paper shape="portrait" size={size}>
          <Box sx={{ height: 12, background: '#DC2626' }} />
          <Lines>
            <Line width="100%" /><Line width="80%" />
            <Line width="100%" /><Line width="50%" />
            <Line width="100%" /><Line width="80%" />
          </Lines>
        </Paper>
      );
    case 'doc':
      return (
        <Paper shape="portrait" size={size}>
          <Box sx={{ height: 5, background: '#4A8FCB' }} />
          <Lines>
            <Line width="60%" height={6} bg="rgba(74,143,203,.5)" />
            <Line width="100%" /><Line width="80%" />
            <Line width="100%" /><Line width="50%" /><Line width="100%" />
          </Lines>
        </Paper>
      );
    case 'ppt':
      return (
        <Paper shape="landscape" size={size}>
          <Lines padding={8} gap={4}>
            <Line width="60%" height={10} bg="rgba(217,119,6,.55)" />
            <Line width="100%" height={3} /><Line width="80%" height={3} />
            <Line width="50%" height={3} /><Line width="100%" height={3} />
          </Lines>
        </Paper>
      );
    case 'html':
      return (
        <Paper shape="window" size={size}>
          <Box sx={{
            height: 12, background: '#EAE2F4', borderBottom: `1px solid ${lineColor}`,
            display: 'flex', alignItems: 'center', gap: '4px', padding: '0 6px',
          }}>
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={{ width: 5, height: 5, borderRadius: '50%', background: '#7C5CE0', opacity: .55 }} />
            ))}
          </Box>
          <Lines padding={8} gap={4}>
            <Line width="50%" height={6} bg="rgba(124,92,224,.6)" />
            <Line width="100%" height={3} /><Line width="80%" height={3} />
            <Line width="100%" height={3} /><Line width="50%" height={3} />
          </Lines>
        </Paper>
      );
    case 'audio': {
      return (
        <Paper shape="portrait" size={size}>
          <Box sx={{ height: 8, background: '#059669' }} />
          <Box sx={{
            height: 'calc(100% - 8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <MusicNoteIcon sx={{ fontSize: size === 'compact' ? 24 : 36, color: '#059669', opacity: 0.85 }} />
          </Box>
        </Paper>
      );
    }
    case 'img':
      return (
        <Paper
          shape="thumbnail"
          size={size}
          sx={{
            background: 'linear-gradient(180deg, #BFE8F5 0%, #BFE8F5 58%, #C8E9B9 58%, #C8E9B9 100%) !important',
          }}
        >
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 5, background: '#DB2777', zIndex: 2 }} />
          <Box sx={{ position: 'absolute', top: 16, right: 18, width: 12, height: 12, borderRadius: '50%', background: '#FBBF24' }} />
          <Box sx={{
            position: 'absolute', bottom: 26, left: 48, width: 0, height: 0,
            borderLeft: '30px solid transparent', borderRight: '30px solid transparent',
            borderBottom: '34px solid #5E5340',
          }} />
          <Box sx={{
            position: 'absolute', bottom: 26, left: 18, width: 0, height: 0,
            borderLeft: '24px solid transparent', borderRight: '24px solid transparent',
            borderBottom: '28px solid #8B7B5C',
          }} />
        </Paper>
      );
    case 'video':
      return (
        <Paper
          shape="thumbnail"
          size={size}
          sx={{ background: '#1A1F2E !important' }}
        >
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: '#DC2626' }} />
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 0, height: 0,
            borderLeft: '14px solid rgba(255,255,255,.92)',
            borderTop: '10px solid transparent',
            borderBottom: '10px solid transparent',
            marginLeft: '3px',
          }} />
          <Box sx={{
            position: 'absolute', bottom: 8, left: 10, right: 10, height: 3,
            borderRadius: '2px', background: 'rgba(255,255,255,.18)', overflow: 'hidden',
          }}>
            <Box sx={{ width: '38%', height: '100%', background: '#DC2626', borderRadius: '2px' }} />
          </Box>
        </Paper>
      );
    case 'view':
      // Authored MDX/pageDoc content. Window-shape Paper (matching `html`)
      // with a purple accent and faint "block" rectangles suggesting
      // interactive content rather than text lines.
      return (
        <Paper shape="window" size={size}>
          <Box sx={{ height: 8, background: '#7C3AED' }} />
          <Box sx={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <Box sx={{ height: 14, width: '60%', background: 'rgba(124,92,224,.25)', borderRadius: '3px' }} />
            <Box sx={{ height: 4, width: '100%', background: lineColor, borderRadius: '2px' }} />
            <Box sx={{ height: 4, width: '85%', background: lineColor, borderRadius: '2px' }} />
            <Box sx={{ height: 18, width: '40%', background: 'rgba(124,92,224,.15)', borderRadius: '3px', marginTop: '4px' }} />
          </Box>
        </Paper>
      );
    case 'archive':
      return (
        <Box sx={{ position: 'relative' }}>
          {/* Back paper (slight offset, suggests a bundle of files) */}
          <Box sx={{
            position: 'absolute', top: 4, left: 4,
            opacity: 0.6, transform: 'rotate(2deg)',
          }}>
            <Paper shape="portrait" size={size}>
              <Box sx={{ height: 10, background: '#92753E', opacity: 0.6 }} />
            </Paper>
          </Box>
          {/* Front paper */}
          <Paper shape="portrait" size={size}>
            <Box sx={{ height: 12, background: '#92753E' }} />
            <Lines>
              <Line width="60%" height={6} bg="rgba(146,117,62,.5)" />
              <Line width="100%" /><Line width="80%" />
              <Line width="50%" /><Line width="100%" />
            </Lines>
          </Paper>
        </Box>
      );
  }
};

export default DocumentIllustration;
